import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="nav-flyout"
//
// Drives the secondary navigation drawers (Reports, Configuration). A trigger
// row opens a server-rendered flyout panel that sits flush against the primary
// sidebar; only one panel is open at a time. Closes on outside click, Escape,
// or Turbo navigation. The "current page" trigger keeps its active styling even
// while no panel is open.
export default class extends Controller {
  static targets = ["panel", "trigger"];

  connect() {
    this.openName = null;
    this.boundOutsideClick = this.handleOutsideClick.bind(this);
    this.boundKeydown = this.handleKeydown.bind(this);
    this.boundClose = this.close.bind(this);
    document.addEventListener("turbo:before-visit", this.boundClose);
    this.syncTriggers(null);
  }

  disconnect() {
    this.stopListening();
    document.removeEventListener("turbo:before-visit", this.boundClose);
  }

  toggle(event) {
    const name = event.params.panel;
    if (this.openName === name) {
      this.close();
    } else {
      this.open(name);
    }
  }

  open(name) {
    this.openName = name;
    this.panelTargets.forEach((panel) => {
      panel.classList.toggle("is-open", panel.dataset.panelName === name);
    });
    this.syncTriggers(name);
    this.startListening();
  }

  close() {
    this.openName = null;
    this.panelTargets.forEach((panel) => panel.classList.remove("is-open"));
    this.syncTriggers(null);
    this.stopListening();
  }

  // The open trigger and the current-page trigger both show the active
  // background; only the open trigger rotates its caret.
  syncTriggers(openName) {
    this.triggerTargets.forEach((trigger) => {
      const isOpen = trigger.dataset.panelName === openName;
      const isCurrent = trigger.dataset.currentPage === "true";
      trigger.classList.toggle("bg-base-content/10", isOpen || isCurrent);
      const caret = trigger.querySelector(".nav-flyout-caret");
      if (caret) caret.classList.toggle("rotate-90", isOpen);
    });
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close();
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close();
    }
  }

  // addEventListener/removeEventListener dedupe by the stable bound reference,
  // so these stay idempotent without a separate "listening" flag.
  startListening() {
    // Defer so the click that opened the panel doesn't immediately close it.
    requestAnimationFrame(() => {
      document.addEventListener("click", this.boundOutsideClick);
      document.addEventListener("keydown", this.boundKeydown);
    });
  }

  stopListening() {
    document.removeEventListener("click", this.boundOutsideClick);
    document.removeEventListener("keydown", this.boundKeydown);
  }
}
